// Image resizing utility for Base64 conversion
// Reduces images to less than 500KB while maintaining quality

interface ResizedImageData {
  data: string; // Base64 encoded image
  contentType: string; // MIME type
  filename: string;
  size: number; // Size in bytes
  dimensions: {
    width: number;
    height: number;
  };
}

export const resizeImage = (
  file: File,
  maxWidth: number = parseInt(
    process.env.REACT_APP_IMAGE_MAX_WIDTH || process.env.IMAG_MAX_WIDTH || "500",
  ),
  maxHeight: number = parseInt(
    process.env.REACT_APP_IMAGE_MAX_HEIGHT || process.env.IMAG_MAX_HEIGHT || "500",
  ),
  quality: number = 0.8,
  maxSizeKB: number = parseInt(
    process.env.REACT_APP_IMAGE_MAX_SIZE_KB || process.env.IMAGE_MAX_SIZE_KB || "500",
  ),
): Promise<ResizedImageData> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      try {
        // If the original file is already within the max size, skip resizing
        const originalSizeKB = file.size / 1024;
        if (originalSizeKB <= maxSizeKB) {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              data: reader.result as string,
              contentType: file.type,
              filename: file.name,
              size: file.size,
              dimensions: {
                width: Math.round(img.width),
                height: Math.round(img.height),
              },
            });
          };
          reader.onerror = () => reject(new Error("FileReader failed"));
          reader.readAsDataURL(file);
          return;
        }

        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > height) {
          if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
          }
        } else {
          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }
        }

        // Ensure dimensions are integers
        width = Math.round(width);
        height = Math.round(height);

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and resize image
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert to blob with quality adjustment
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas to blob conversion failed"));
              return;
            }

            // Check if size is acceptable, if not, reduce quality
            const sizeKB = blob.size / 1024;

            if (sizeKB > maxSizeKB) {
              // Recursively reduce quality until size is acceptable
              const newQuality = Math.max(0.1, quality * 0.8);
              resizeImage(file, maxWidth, maxHeight, newQuality, maxSizeKB)
                .then(resolve)
                .catch(reject);
              return;
            }

            // Convert blob to Base64
            const reader = new FileReader();
            reader.onload = () => {
              const base64String = reader.result as string;

              resolve({
                data: base64String,
                contentType: file.type,
                filename: file.name,
                size: blob.size,
                dimensions: { width, height },
              });
            };
            reader.onerror = () => reject(new Error("FileReader failed"));
            reader.readAsDataURL(blob);
          },
          file.type,
          quality,
        );
      } catch (error) {
        reject(new Error(`Image processing failed: ${error}`));
      }
    };

    img.onerror = () => reject(new Error("Image loading failed"));
    img.src = URL.createObjectURL(file);
  });
};

// Batch resize multiple images
export const resizeImages = async (
  files: File[],
  maxWidth: number = 500,
  maxHeight: number = 500,
  quality: number = 0.8,
  maxSizeKB: number = 500,
): Promise<ResizedImageData[]> => {
  const resizePromises = files.map((file) =>
    resizeImage(file, maxWidth, maxHeight, quality, maxSizeKB),
  );

  return Promise.all(resizePromises);
};

// Validate image file before processing
export const validateImageFile = (file: File): string | null => {
  // Check file type
  if (!file.type.startsWith("image/")) {
    return "Only image files are allowed";
  }

  // Check file size (10MB max before processing)
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxFileSize) {
    return "File size must be less than 10MB";
  }

  return null;
};

// Get file size in human readable format
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
